import React from 'react';
import PropTypes from 'prop-types';
import { Typography, useThemeTokens, applyShadowToken } from '@telus-uds/components-base';
import styled, { css } from 'styled-components';
import { useTableContext } from './Table';
import { jsx as _jsx } from "react/jsx-runtime";
const HEADER_TYPE = {
  HEADING: 'heading',
  SUBHEADING: 'subHeading',
  ROWHEADING: 'rowHeading'
};
const stickyStyles = _ref => {
  let {
    cellStickyShadow,
    stickyBackgroundColor,
    type
  } = _ref;
  return css(["position:sticky;left:0;z-index:2;clip-path:inset(0 -8px 0 0);", " &::before{content:'';box-shadow:", ";position:absolute;top:0;left:0;right:0;bottom:0;pointer-events:none;}"], type !== HEADER_TYPE.SUBHEADING && type !== HEADER_TYPE.HEADING ? `background-color: ${stickyBackgroundColor};` : undefined, cellStickyShadow);
};
const sharedStyles = /*#__PURE__*/css(["", ""], _ref2 => {
  let {
    isSticky,
    align,
    cellPaddingTop,
    cellPaddingRight,
    cellPaddingBottom,
    cellPaddingLeft,
    cellBorderWidth,
    cellBorderColor,
    cellBorderTopWidth,
    cellMinWidth,
    cellBackground,
    cellStickyShadow,
    stickyBackgroundColor,
    type
  } = _ref2;
  return css(["text-align:", ";min-width:", "px;padding:", "px ", "px ", "px ", "px;background-color:", ";", ";border-style:", ";border-color:", ";border-width:", ";border-top-width:", ";"], align, cellMinWidth, cellPaddingTop, cellPaddingRight, cellPaddingBottom, cellPaddingLeft, cellBackground, isSticky && stickyStyles({
    type,
    cellStickyShadow,
    stickyBackgroundColor
  }), cellBorderWidth || cellBorderTopWidth ? 'solid' : 'none', cellBorderColor, cellBorderWidth, cellBorderTopWidth || cellBorderWidth);
});
const createStyledCell = htmlElement => styled[htmlElement].withConfig({
  displayName: "Cell__createStyledCell",
  componentId: "components-web__sc-ltgfic-0"
})(["", ";box-shadow:", ";", ""], sharedStyles, _ref3 => {
  let {
    cellBoxShadowColor,
    type
  } = _ref3;
  return type === HEADER_TYPE.HEADING ? `inset 0 1px 0 ${cellBoxShadowColor}, inset 0 -1px 0 ${cellBoxShadowColor}` : `inset 0 1px 0 ${cellBoxShadowColor}`;
}, _ref4 => {
  let {
    display
  } = _ref4;
  return display && `*:not(:empty) {
    display: ${display};
  }`;
});
const StyledHeaderCell = createStyledCell('th');
const StyledDataCell = createStyledCell('td');
const Cell = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  let {
    children,
    isFirstInRow,
    align = 'left',
    tokens: cellTokens,
    type = 'default'
  } = _ref5;
  const {
    text,
    isScrollable: isTableScrollable,
    tokens: tableTokens,
    spacing,
    variant
  } = useTableContext();
  const themeTokens = {
    ...tableTokens,
    ...cellTokens
  };
  const {
    cellMinWidth,
    cellBoxShadowColor,
    cellBackground,
    cellStickyShadow,
    cellPaddingTop,
    cellPaddingRight,
    cellPaddingLeft,
    cellPaddingBottom,
    cellBorderWidth,
    cellBorderColor,
    cellBorderTopWidth,
    fontName,
    fontWeight,
    fontSize,
    lineHeight,
    stickyBackgroundColor,
    fontColor,
    display
  } = useThemeTokens('Table', themeTokens, {
    spacing,
    type,
    text,
    ...variant
  });
  const sharedProps = {
    align,
    isSticky: isTableScrollable && isFirstInRow,
    cellBackground,
    cellMinWidth,
    cellStickyShadow: applyShadowToken(cellStickyShadow).boxShadow,
    cellPaddingTop,
    cellPaddingRight,
    cellPaddingLeft,
    cellPaddingBottom,
    cellBorderWidth,
    cellBorderColor,
    cellBorderTopWidth,
    stickyBackgroundColor,
    cellBoxShadowColor,
    display,
    ref
  };
  const typographyTokens = {
    fontName,
    fontWeight,
    fontSize,
    lineHeight,
    color: fontColor
  };
  switch (type) {
    case HEADER_TYPE.HEADING:
      return /*#__PURE__*/_jsx(StyledHeaderCell, {
        scope: "col",
        ...sharedProps,
        type: type,
        children: /*#__PURE__*/_jsx(Typography, {
          tokens: typographyTokens,
          children: children
        })
      });
    case HEADER_TYPE.SUBHEADING:
      return /*#__PURE__*/_jsx(StyledHeaderCell, {
        scope: "col",
        ...sharedProps,
        type: type,
        children: /*#__PURE__*/_jsx(Typography, {
          tokens: typographyTokens,
          children: children
        })
      });
    case HEADER_TYPE.ROWHEADING:
      return /*#__PURE__*/_jsx(StyledHeaderCell, {
        scope: "row",
        ...sharedProps,
        type: type,
        children: /*#__PURE__*/_jsx(Typography, {
          tokens: typographyTokens,
          children: children
        })
      });
    default:
      return /*#__PURE__*/_jsx(StyledDataCell, {
        ...sharedProps,
        children: /*#__PURE__*/_jsx(Typography, {
          tokens: typographyTokens,
          children: children
        })
      });
  }
});
Cell.displayName = 'Cell';
Cell.propTypes = {
  type: PropTypes.oneOf(['default', 'heading', 'subHeading', 'rowHeading']),
  tokens: PropTypes.object,
  children: PropTypes.node,
  /**
   * @ignore
   * Used internally for making the first column sticky
   */
  isFirstInRow: PropTypes.bool,
  /**
   * Defines the text alignment within the cell
   */
  align: PropTypes.oneOf(['left', 'center', 'right'])
};
export default Cell;